import type { GetStaticPaths, GetStaticProps } from "next";
import type { SearchResult, TownSummary, TradeCount } from "@/lib/construction/types";
import type { Faq } from "@/lib/construction/content";

import { Meta } from "@/components/seo/Meta";
import { Header } from "@/components/sections/Header";
import { Footer } from "@/components/sections/Footer";
import { FaqBlock } from "@/components/sections/content/FaqBlock";
import { PlaceHero, DirectorySection } from "@/components/construction/PlaceHero";
import { CompanyList } from "@/components/construction/CompanyList";
import { TownList } from "@/components/construction/TownList";
import { TradeList } from "@/components/construction/TradeList";
import { TownPagination } from "@/components/construction/TownPagination";
import { ServiceLinks } from "@/components/construction/ServiceLinks";
import { DirectoryAttribution } from "@/components/construction/Attribution";
import { townAnswer, townFaqs } from "@/lib/construction/content";
import { townMeta } from "@/lib/construction/meta";
import { collectionGraph, graphHtml } from "@/lib/construction/schema";
import { DIRECTORY_PATH, getRegion, isRegionSlug, regionHref, townHref, townKey } from "@/lib/construction/places";
import { countCompanies, formatCount } from "@/lib/construction/normalise";
import { getTrade } from "@/lib/construction/trades";
import { DIRECTORY_REVALIDATE } from "@/lib/construction/runtime";

type NearbyTown = TownSummary & { distanceKm: number };

type TownPageProps = {
  town: TownSummary;
  trades: TradeCount[];
  nearby: NearbyTown[];
  listings: SearchResult;
  answer: string;
  faqs: Faq[];
  meta: { title: string; description: string };
};

export default function TownPage({ town, trades, nearby, listings, answer, faqs, meta }: TownPageProps) {
  const region = getRegion(town.region);
  const href = townHref(town.region, town.slug);
  // Inline CMS editing: the town name is a directory_towns value.
  const townCms = town.id ? `directory_towns:${town.id}:name` : undefined;
  const TownName = () => (townCms ? <span data-mdlfy-cms={townCms}>{town.name}</span> : <>{town.name}</>);
  const crumbs = [
    { name: "Home", href: "/" },
    { name: "Construction companies", href: DIRECTORY_PATH },
    { name: region?.name ?? "Region", href: regionHref(town.region) },
    { name: town.name, href, cms: townCms },
  ];
  const title = `Construction Companies in ${town.name}`;
  const topTrade = trades[0] ? getTrade(trades[0].slug) : null;

  const graph = collectionGraph({
    href,
    name: title,
    description: meta.description,
    crumbs,
    listings: listings.items,
    totalListings: town.count,
    faqs,
    about: { type: "City", name: town.name },
  });

  return (
    <>
      <Meta absoluteTitle={meta.title} description={meta.description} canonical={href} ogImageAlt={title} />
      <Header />
      <main id="main">
        <PlaceHero
          crumbs={crumbs}
          eyebrow={
            <>
              <TownName />, {region?.longName ?? "UK"}
            </>
          }
          title={
            <>
              Construction Companies in <TownName />
            </>
          }
          answer={answer}
        >
          <DirectoryAttribution className="mt-6" />
        </PlaceHero>

        <DirectorySection
          id="companies"
          title={
            <>
              Which construction companies are in <TownName />?
            </>
          }
          intro={
            <>
              The directory lists {countCompanies(town.count)} in <TownName />. Companies with verified size data appear first, then the rest in A
              to Z order.
            </>
          }
        >
          <CompanyList initial={listings.items} total={listings.total} filter={{ towns: [town.key] }} label={`Construction companies in ${town.name}`} />
          <TownPagination region={town.region} townSlug={town.slug} townName={town.name} total={listings.total} />
        </DirectorySection>

        {trades.length && topTrade ? (
          <DirectorySection
            id="trades"
            tone="muted"
            title={
              <>
                Which construction trades are found in <TownName />?
              </>
            }
            intro={
              <>
                The most common construction trade in <TownName /> is {topTrade.plural}, with {formatCount(trades[0].count)}{" "}
                {trades[0].count === 1 ? "company" : "companies"} listed. Each trade links to what a website for that trade needs.
              </>
            }
          >
            <TradeList trades={trades} place={town.name} />
          </DirectorySection>
        ) : null}

        {nearby.length ? (
          <DirectorySection
            id="nearby"
            title={
              <>
                Which towns near <TownName /> have construction companies?
              </>
            }
            intro={
              <>
                The closest towns to <TownName /> with construction companies in the directory are{" "}
                {nearby.slice(0, 2).map((t, i) => (
                  <span key={t.key}>
                    {i > 0 ? " and " : null}
                    {t.id ? <span data-mdlfy-cms={`directory_towns:${t.id}:name`}>{t.name}</span> : t.name} (about {Math.round(t.distanceKm)} km away)
                  </span>
                ))}
                .
              </>
            }
          >
            <TownList towns={nearby} label={`Towns near ${town.name}`} showDistance />
          </DirectorySection>
        ) : null}

        {faqs.length ? (
          <FaqBlock
            title={
              <>
                Construction companies in <TownName />: questions
              </>
            }
            faqs={faqs}
          />
        ) : null}

        <ServiceLinks place={town.name} trades={trades.map((t) => t.slug)} />
      </main>
      <Footer />
      <script type="application/ld+json" dangerouslySetInnerHTML={{ __html: graphHtml(graph) }} />
    </>
  );
}

export const getStaticPaths: GetStaticPaths = async () => ({ paths: [], fallback: "blocking" });

export const getStaticProps: GetStaticProps<TownPageProps> = async ({ params }) => {
  const regionParam = typeof params?.region === "string" ? params.region : "";
  const townParam = typeof params?.town === "string" ? params.town : "";
  if (!isRegionSlug(regionParam) || !townParam) return { notFound: true, revalidate: DIRECTORY_REVALIDATE };

  const { maybeStartImport } = await import("@/lib/construction/import");
  const data = await import("@/lib/construction/data");
  void maybeStartImport();

  const key = townKey(regionParam, townParam);
  const town = await data.getTown(key);
  if (!town || town.count === 0) return { notFound: true, revalidate: DIRECTORY_REVALIDATE };

  const [trades, listings, nearby] = await Promise.all([
    data.getTradeCounts({ townKey: key }),
    data.searchListings({ towns: [key], sort: "size", limit: 30 }),
    town.lat !== null && town.lng !== null ? data.getNearbyTowns(town.lat, town.lng, key, 8) : Promise.resolve([] as NearbyTown[]),
  ]);

  return {
    props: {
      town,
      trades,
      nearby,
      listings,
      answer: townAnswer(town, trades, nearby),
      faqs: townFaqs(town, trades, nearby),
      meta: townMeta(town, trades),
    },
    revalidate: DIRECTORY_REVALIDATE,
  };
};
